import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import { Descriptions } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Approval, ApprovalStatus } from 'api/approval';
import { ProjectId } from 'api/models';
import { Alert as AlertAtom } from 'components/atoms/alert';
import { Button } from 'components/atoms/button';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { Trigger } from 'components/molecules/squad-picker/trigger';
import { userOption } from 'components/molecules/squad-picker/utils';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUsersQuery } from 'queries/members';
import styles from './approval.module.css';
import { MessageEditor } from './message-editor';

const Alert = styled(AlertAtom)`
  margin-bottom: var(--spacing-03);
`;

interface ApprovalFormProps {
  data: Approval;
  projectId: number;
}

export const ApprovalForm: FC<ApprovalFormProps> = (props) => {
  const { data, projectId } = props;
  const { user: currentUser } = useUser();
  const { data: allUsers } = useUsersQuery();
  const isPrivate = data.private;
  const approverIds = data.requestTo.map((r) => r.userId);

  const comment =
    data.comments?.find((c) => c.createdBy === data.requestBy.userId)
      ?.content ?? '';

  const translatedCommentPlaceholderText = useTranslation(
    'approvals.comment.placeholder',
  );

  const isWaiting = currentUser && currentUser.userId === data.requestBy.userId;
  let actionNode;

  const isFullyApproved =
    data.requestTo.length > 0 &&
    data.requestTo.filter((r) => r.approvalStatus === ApprovalStatus.Approved)
      .length === data.requestTo.length;

  if (isFullyApproved) {
    actionNode = (
      <Alert
        message={<FormattedMessage id="approvals.approved" />}
        type="success"
        showIcon
      />
    );
  } else if (isWaiting) {
    const isPending =
      data.requestTo?.filter(
        (r) => r.approvalStatus === ApprovalStatus.ApprovalRequested,
      ).length === data.requestTo.length;

    if (isPending) {
      actionNode = (
        <Alert
          message={<FormattedMessage id="approvals.waiting" />}
          type="info"
          showIcon
        />
      );
    } else {
      const approvedBy = data.requestTo.filter(
        (r) => r.approvalStatus === ApprovalStatus.Approved,
      ).length;

      actionNode = (
        <Alert
          message={
            <FormattedMessage
              id="approvals.approvedBy"
              values={{ result: approvedBy, total: data.requestTo.length }}
            />
          }
          type="info"
          showIcon
        />
      );
    }
  }

  const approversOpts = useMemo(() => {
    return (allUsers || [])
      .filter((u) => approverIds.includes(u.userId))
      .map((u) => userOption(u));
  }, [allUsers, approverIds]);

  return (
    <>
      <div className="caption text-03">
        <FormattedMessage
          id={isPrivate ? 'approvals.private.request' : 'approvals.requested'}
        />
      </div>
      <div className={styles.fields_container}>
        <Descriptions colon={false}>
          <Descriptions.Item
            className={styles.field}
            label={
              <span className="label text-02">
                <FormattedMessage id="type" />
              </span>
            }
          >
            <Button
              icon={isPrivate ? <ViewOff16 /> : <View16 />}
              type="text"
              disabled
            >
              <FormattedMessage id={isPrivate ? 'private' : 'public'} />
            </Button>
          </Descriptions.Item>
        </Descriptions>
        <Descriptions colon={false}>
          <Descriptions.Item
            className={styles.field}
            label={
              <span className="label text-02">
                <FormattedMessage id="approvals.approvers" />
              </span>
            }
          >
            <div className={styles.approvers}>
              <Button type="text" size="small" disabled>
                <Trigger value={approversOpts} />
              </Button>
            </div>
          </Descriptions.Item>
        </Descriptions>
        {data.dueDate && (
          <Descriptions colon={false}>
            <Descriptions.Item
              className={styles.field}
              label={
                <span className="label text-02">
                  <FormattedMessage id="due_date" />
                </span>
              }
            >
              <div className={styles.due_date}>
                <Button type="text" size="small" disabled>
                  <HumanisedDate timestamp={moment(data.dueDate)} />
                </Button>
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
        {comment ? (
          <MessageEditor
            projectId={ProjectId(projectId)}
            className={classNames(styles.approval_request_comment, {
              [styles.private]: isPrivate,
            })}
            placeholder={translatedCommentPlaceholderText}
            readonly
            data={comment}
          />
        ) : null}
      </div>
      {actionNode}
    </>
  );
};
